import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import ContentDetail from "src/pages/ContentDetail";
import ContentList from "src/pages/ContentList";
import { useDispatch, useSelector } from "react-redux";
import { post } from "src/services/content.service";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { Tab, Tabs, Box as MuiBox, Typography } from "@mui/material";
import SwipeableViews from "react-swipeable-views";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { showAlert } from "src/redux/slices/AlertSlice";
import { useTranslation } from "react-i18next";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const SortableTab = ({ sortableId, label, tooltip, disabled, ...props }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
    } =
        useSortable({ id: sortableId, disabled });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: disabled ? "default" : "grab",
        display: "flex",
        alignItems: "center",
        gap: 6,
    };
    return (
        <Tab
            ref={setNodeRef}
            style={style}
            title={tooltip || ""}
            label={
                <MuiBox sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                    {!disabled && (
                        <MuiBox
                            component="span"
                            ref={setActivatorNodeRef}
                            {...attributes}
                            {...listeners}
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                color: "text.disabled",
                            }}
                        >
                            <DragIndicatorIcon fontSize="small" />
                        </MuiBox>
                    )}
                    <span>{label}</span>
                </MuiBox>
            }
            {...props}
        />
    );
};

const TabLayoutArticles = ({ category, tab }) => {
    const [articles, setArticles] = useState([]);
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user && user.is_staff === true;
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [value, setValue] = useState(0);
    const [activeTabId, setActiveTabId] = useState(null);
    const initialTabOrderRef = useRef(null);
    const { t } = useTranslation();
    const canReorderTabs = useMemo(
        () => isAdmin && articles.every((a) => a && a.nav_id),
        [isAdmin, articles]
    );
    const sortableTabIds = useMemo(
        () => articles.filter((a) => a && a.nav_id).map((a) => a.nav_id),
        [articles]
    );
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    useEffect(() => {
        const fetchArticles = async () => {
            const body = JSON.stringify({
                category: category,
                sort_by: "popular",
            });
            try {
                const res = await post(dispatch, `/api/content/content/`, body);
                setArticles(res.data);
            } catch (err) {
                console.log(err);
            }
        };

        fetchArticles();
    }, [category, dispatch]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get("tab");
        let tabIndex = 0;
        if (articles.length > 0) {
            if (tabParam) {
                const tabNum = Number(tabParam);
                if (tabNum < 1 || tabNum > articles.length) {
                    navigate("/404");
                } else {
                    tabIndex = tabNum - 1;
                }
            }
            setValue(tabIndex);
        }
        // eslint-disable-next-line
    }, [location.search, articles.length]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        window.history.pushState(
            "",
            "",
            `${location.pathname}?tab=${newValue + 1}`
        );
    };

    const handleChangeIndex = (index) => {
        setValue(index);
        window.history.pushState("", "", `${location.pathname}?tab=${index + 1}`);
    };

    const handleTabDndOver = useCallback(({ active, over }) => {
        if (!over || active.id === over.id || !canReorderTabs) {
            return;
        }
        const oldIndex = articles.findIndex((a) => a.nav_id === active.id);
        const newIndex = articles.findIndex((a) => a.nav_id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }
        const newArticles = arrayMove(articles, oldIndex, newIndex);
        setArticles(newArticles);
    }, [articles, canReorderTabs]);

    const handleTabDndStart = useCallback(({ active }) => {
        if (!canReorderTabs) {
            return;
        }
        setActiveTabId(active.id);
        initialTabOrderRef.current = new Map(
            articles.map((a, idx) => [a.nav_id, idx])
        );
    }, [canReorderTabs, sortableTabIds]);

    const handleTabDndEnd = useCallback(
        async ({ active, over }) => {
            setActiveTabId(null);
            if (!over || !canReorderTabs) {
                return;
            }
            const oldIndex = articles.findIndex((a) => a.nav_id === active.id);
            const newIndex = articles.findIndex((a) => a.nav_id === over.id);
            if (oldIndex < 0 || newIndex < 0) {
                return;
            }
            const newArticles = arrayMove(articles, oldIndex, newIndex);
            setArticles(newArticles);
            setValue(newArticles.findIndex((a) => a.nav_id === active.id));
            window.history.pushState(
                "",
                "",
                `${location.pathname}?tab=${newIndex + 1}`
            );

            const oldOrder = initialTabOrderRef.current || new Map();
            const modified = newArticles
                .map((a, idx) => ({
                    id: a.nav_id,
                    parent: a.nav_parent_id,
                    position: idx,
                    title: a.content?.title,
                    type: a.content?.type,
                    changed: true,
                    _old: oldOrder.get(a.nav_id),
                }))
                .filter((row) => row._old !== row.position)
                .map(({ _old, ...row }) => row);
            if (!modified.length) {
                return;
            }
            const payload = {
                data: {
                    userId: user ? user.id : null,
                    id: -1,
                    type: "navigation",
                    status: false,
                    title: "",
                    body: null,
                    sticky: 0,
                    news: true,
                    groups: [],
                },
                nav: {
                    modified,
                    removed: [],
                },
            };
            try {
                const res = await post(dispatch, `/api/content/admin/add/`, payload);
                dispatch(
                    showAlert({
                        message: t("ContentList.reorder.success"),
                        severity: "success",
                    })
                );
            } catch (err) {
                dispatch(
                    showAlert({
                        message:
                            (err.response &&
                                err.response.data &&
                                (err.response.data.nav ||
                                    err.response.data.error)) ||
                            err.message ||
                            t("ContentList.reorder.error"),
                        severity: "error",
                    })
                );
            }
        },
        [articles, canReorderTabs, dispatch, location.pathname, user, navigate, t]
    );

    return (
        <Box
            sx={{
                width: "100%",
                display: "inline",
            }}
        >
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleTabDndStart}
                onDragOver={handleTabDndOver}
                onDragEnd={handleTabDndEnd}
            >
                <SortableContext
                    items={sortableTabIds}
                    strategy={horizontalListSortingStrategy}
                >
                    <Tabs
                        orientation="horizontal"
                        value={value}
                        onChange={handleChange}
                        aria-label="basic tabs example"
                        sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            "& .MuiTabs-flexContainer": {
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "flex-start",
                            },
                        }}
                    >
                        {articles.map((article, index) => {
                            if (!article.nav_id) {
                                return (
                                    <Tab
                                        key={article.content.slug || index}
                                        label={article.content.title}
                                        {...a11yProps(index)}
                                    />
                                );
                            }
                            return (
                                <SortableTab
                                    key={article.nav_id}
                                    sortableId={article.nav_id}
                                    label={article.content.title}
                                    tooltip={article.content.tooltip}
                                    disabled={!canReorderTabs}
                                    {...a11yProps(index)}
                                />
                            );
                        })}
                    </Tabs>
                </SortableContext>
                <DragOverlay>
                    {activeTabId ? (
                        <MuiBox
                            sx={{
                                px: 2,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: "background.paper",
                                boxShadow: 6,
                                border: "1px solid",
                                borderColor: "divider",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <DragIndicatorIcon fontSize="small" />
                            <Typography variant="subtitle2">
                                {articles.find((a) => a.nav_id === activeTabId)?.content?.title || ""}
                            </Typography>
                        </MuiBox>
                    ) : null}
                </DragOverlay>
            </DndContext>
            <SwipeableViews
                axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                index={value}
                onChangeIndex={handleChangeIndex}
            >
                {articles.map((article, index) => {
                    return (
                        <TabPanel
                            value={value}
                            index={index}
                            key={article.content.slug}
                            dir={theme.direction}
                        >
                            {article.content.type === "content_list" ? (
                                <ContentList
                                    label={article.content.title}
                                    tooltip={article.content.tooltip}
                                    altSlug={article.content.slug}
                                    endpoint="/api/content/list/"
                                    tab={index}
                                />
                            ) : (
                                <ContentDetail
                                    label={article.content.title}
                                    tooltip={article.content.tooltip}
                                    altSlug={article.content.slug}
                                    endpoint="/api/content/list/"
                                />
                            )}
                        </TabPanel>
                    );
                })}
            </SwipeableViews>
        </Box>
    );
};

TabLayoutArticles.propTypes = {
    category: PropTypes.string.isRequired,
};

export default TabLayoutArticles;
